// ============================================================================
// GESTIMMONY — Portail Locataire : Login (email+password) + magic link
// ============================================================================
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Mail, KeyRound, Sparkles } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export default function PortalLoginPage() {
  const [mode, setMode] = useState("password"); // "password" | "magic"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const navigate = useNavigate();

  const loginPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/portal/login", { email, password });
      toast.success("Connecté");
      navigate("/portail");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Identifiants invalides");
    } finally { setBusy(false); }
  };

  const requestMagic = async (e) => {
    e.preventDefault();
    setBusy(true);
    setDevLink(null);
    try {
      const { data } = await api.post("/portal/magic-link", { email });
      toast.success("Si un compte existe, un lien vous a été envoyé.");
      if (data.dev_link) setDevLink(data.dev_link);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="portal-login-page">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-teal-700 to-emerald-800 text-white p-12 relative overflow-hidden">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-md bg-white flex items-center justify-center p-1">
              <LogoMark className="h-7" />
            </span>
            <div className="font-heading font-bold text-lg tracking-tight">Gestimmony · Espace locataire</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur text-xs font-semibold px-3 py-1.5 mb-4">
            <Sparkles className="h-3.5 w-3.5" /> Portail sécurisé
          </div>
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight">
            Vos quittances, vos documents,<br />un incident en 30 secondes.
          </h1>
          <p className="mt-4 text-white/80 leading-relaxed">
            Retrouvez toutes vos quittances de loyer, téléchargez votre bail signé, et signalez
            un problème directement à votre bailleur — sans rien perdre.
          </p>
        </div>
        <div className="text-xs text-white/60">© 2026 Gestimmony — Espace locataire</div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <LogoMark className="h-9" />
            <div className="font-heading font-bold text-lg">Gestimmony — Locataire</div>
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {mode === "password" ? "Connexion locataire" : "Recevoir un lien de connexion"}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {mode === "password"
              ? "Utilisez l'email et le mot de passe reçus lors de votre invitation."
              : "Nous vous enverrons un lien à usage unique valable 24h."}
          </p>

          <Card className="mt-6 p-6 border-slate-200 shadow-none">
            {mode === "password" ? (
              <form onSubmit={loginPassword} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input data-testid="portal-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Mot de passe</Label>
                  <Input data-testid="portal-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" />
                </div>
                <Button data-testid="portal-login-submit" type="submit" disabled={busy} className="w-full bg-[hsl(var(--primary))]">
                  <LogIn className="h-4 w-4 mr-2" /> {busy ? "Connexion…" : "Se connecter"}
                </Button>
                <div className="text-center">
                  <button type="button" onClick={() => setMode("magic")} className="text-xs text-slate-500 hover:text-teal-700 hover:underline">
                    Mot de passe oublié ? Recevoir un lien
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={requestMagic} className="space-y-4">
                <div>
                  <Label>Votre email</Label>
                  <Input data-testid="portal-magic-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
                </div>
                <Button data-testid="portal-magic-submit" type="submit" disabled={busy} className="w-full bg-[hsl(var(--primary))]">
                  <Mail className="h-4 w-4 mr-2" /> {busy ? "Envoi…" : "M'envoyer un lien"}
                </Button>
                {devLink && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs" data-testid="portal-dev-link-box">
                    <p className="font-semibold text-amber-900 mb-1">🛠️ Mode dev — Lien direct :</p>
                    <a href={devLink} className="text-teal-700 break-all hover:underline">{devLink}</a>
                  </div>
                )}
                <div className="text-center">
                  <button type="button" onClick={() => setMode("password")} className="text-xs text-slate-500 hover:text-teal-700 hover:underline">
                    ← Retour à la connexion classique
                  </button>
                </div>
              </form>
            )}
          </Card>

          <div className="mt-6 flex items-center text-xs text-slate-500">
            <Link to="/" data-testid="portal-login-back-home" className="hover:text-teal-700">← Gestimmony</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
